import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { useMemo } from "react";
import * as THREE from "three";

export type Atom3D = {
  id: string;
  el: "C" | "H" | "O" | "N";
  pos: [number, number, number];
};

export type Bond3D = { from: string; to: string; order?: 1 | 2 };

export type Molecule3D = {
  atoms: Atom3D[];
  bonds: Bond3D[];
};

const ELEMENT_COLOR: Record<Atom3D["el"], string> = {
  C: "#3a3a3a",
  H: "#e8e8e8",
  O: "#e0524f",
  N: "#5b8def",
};

const ELEMENT_RADIUS: Record<Atom3D["el"], number> = {
  C: 0.32,
  H: 0.2,
  O: 0.3,
  N: 0.3,
};

function Bond({
  a,
  b,
  order = 1,
}: {
  a: [number, number, number];
  b: [number, number, number];
  order?: 1 | 2;
}) {
  const { position, quaternion, length } = useMemo(() => {
    const start = new THREE.Vector3(...a);
    const end = new THREE.Vector3(...b);
    const dir = new THREE.Vector3().subVectors(end, start);
    const len = dir.length();
    const mid = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
    const up = new THREE.Vector3(0, 1, 0);
    const quat = new THREE.Quaternion().setFromUnitVectors(up, dir.clone().normalize());
    return { position: mid.toArray() as [number, number, number], quaternion: quat, length: len };
  }, [a, b]);

  if (order === 2) {
    // Compute a perpendicular offset in world space
    const dir = new THREE.Vector3(b[0] - a[0], b[1] - a[1], b[2] - a[2]).normalize();
    const ref = Math.abs(dir.y) > 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
    const perp = new THREE.Vector3().crossVectors(dir, ref).normalize().multiplyScalar(0.09);
    return (
      <>
        <mesh position={[position[0] + perp.x, position[1] + perp.y, position[2] + perp.z]} quaternion={quaternion}>
          <cylinderGeometry args={[0.05, 0.05, length, 12]} />
          <meshStandardMaterial color="#9aa0a6" />
        </mesh>
        <mesh position={[position[0] - perp.x, position[1] - perp.y, position[2] - perp.z]} quaternion={quaternion}>
          <cylinderGeometry args={[0.05, 0.05, length, 12]} />
          <meshStandardMaterial color="#9aa0a6" />
        </mesh>
      </>
    );
  }

  return (
    <mesh position={position} quaternion={quaternion}>
      <cylinderGeometry args={[0.07, 0.07, length, 14]} />
      <meshStandardMaterial color="#9aa0a6" />
    </mesh>
  );
}

export function BallStickViewer({
  molecule,
  height = 240,
  ariaLabel,
}: {
  molecule: Molecule3D;
  height?: number;
  ariaLabel: string;
}) {
  const atomMap = useMemo(() => new Map(molecule.atoms.map((a) => [a.id, a])), [molecule]);

  return (
    <div
      style={{ height }}
      className="w-full rounded-md overflow-hidden bg-gradient-to-b from-muted/40 to-muted/10 border border-border"
      role="img"
      aria-label={ariaLabel}
    >
      <Canvas camera={{ position: [0, 0, 5], fov: 45 }}>
        <ambientLight intensity={0.6} />
        <directionalLight position={[5, 5, 5]} intensity={0.8} />
        <directionalLight position={[-5, -3, -2]} intensity={0.3} />
        {molecule.atoms.map((a) => (
          <mesh key={a.id} position={a.pos}>
            <sphereGeometry args={[ELEMENT_RADIUS[a.el], 24, 24]} />
            <meshStandardMaterial color={ELEMENT_COLOR[a.el]} roughness={0.35} metalness={0.05} />
          </mesh>
        ))}
        {molecule.bonds.map((b, i) => {
          const fa = atomMap.get(b.from);
          const ta = atomMap.get(b.to);
          if (!fa || !ta) return null;
          return <Bond key={i} a={fa.pos} b={ta.pos} order={b.order} />;
        })}
        <OrbitControls enablePan={false} enableZoom={true} />
      </Canvas>
    </div>
  );
}

// Pre-defined molecules for the stereo examples
// Coordinates are approximate (Ångström-like, scaled). Hydrogens included.

export const MOLECULES: Record<string, Molecule3D> = {
  "cis-2-butene": {
    atoms: [
      // Central C=C along x-axis
      { id: "c2", el: "C", pos: [-0.67, 0, 0] },
      { id: "c3", el: "C", pos: [0.67, 0, 0] },
      // Methyl carbons on the SAME side (both +y)
      { id: "c1", el: "C", pos: [-1.43, 1.15, 0] },
      { id: "c4", el: "C", pos: [1.43, 1.15, 0] },
      // Vinyl hydrogens on the opposite side (both -y)
      { id: "h_c2", el: "H", pos: [-1.18, -0.91, 0] },
      { id: "h_c3", el: "H", pos: [1.18, -0.91, 0] },
      // Methyl hydrogens (3 each, tetrahedral approx)
      { id: "h1a", el: "H", pos: [-2.15, 1.05, 0.85] },
      { id: "h1b", el: "H", pos: [-2.15, 1.05, -0.85] },
      { id: "h1c", el: "H", pos: [-0.83, 2.05, 0] },
      { id: "h4a", el: "H", pos: [2.15, 1.05, 0.85] },
      { id: "h4b", el: "H", pos: [2.15, 1.05, -0.85] },
      { id: "h4c", el: "H", pos: [0.83, 2.05, 0] },
    ],
    bonds: [
      { from: "c2", to: "c3", order: 2 },
      { from: "c2", to: "c1" },
      { from: "c3", to: "c4" },
      { from: "c2", to: "h_c2" },
      { from: "c3", to: "h_c3" },
      { from: "c1", to: "h1a" },
      { from: "c1", to: "h1b" },
      { from: "c1", to: "h1c" },
      { from: "c4", to: "h4a" },
      { from: "c4", to: "h4b" },
      { from: "c4", to: "h4c" },
    ],
  },

  "trans-2-butene": {
    atoms: [
      { id: "c2", el: "C", pos: [-0.67, 0, 0] },
      { id: "c3", el: "C", pos: [0.67, 0, 0] },
      // Methyl carbons on OPPOSITE sides
      { id: "c1", el: "C", pos: [-1.43, 1.15, 0] },
      { id: "c4", el: "C", pos: [1.43, -1.15, 0] },
      // Vinyl hydrogens opposite their methyls
      { id: "h_c2", el: "H", pos: [-1.18, -0.91, 0] },
      { id: "h_c3", el: "H", pos: [1.18, 0.91, 0] },
      { id: "h1a", el: "H", pos: [-2.15, 1.05, 0.85] },
      { id: "h1b", el: "H", pos: [-2.15, 1.05, -0.85] },
      { id: "h1c", el: "H", pos: [-0.83, 2.05, 0] },
      { id: "h4a", el: "H", pos: [2.15, -1.05, 0.85] },
      { id: "h4b", el: "H", pos: [2.15, -1.05, -0.85] },
      { id: "h4c", el: "H", pos: [0.83, -2.05, 0] },
    ],
    bonds: [
      { from: "c2", to: "c3", order: 2 },
      { from: "c2", to: "c1" },
      { from: "c3", to: "c4" },
      { from: "c2", to: "h_c2" },
      { from: "c3", to: "h_c3" },
      { from: "c1", to: "h1a" },
      { from: "c1", to: "h1b" },
      { from: "c1", to: "h1c" },
      { from: "c4", to: "h4a" },
      { from: "c4", to: "h4b" },
      { from: "c4", to: "h4c" },
    ],
  },

  // (R)-Lactic acid: chiral C at origin with tetrahedral substituents
  // Tetrahedral directions:
  //   v1 = ( 1,  1,  1)   → CH3
  //   v2 = (-1, -1,  1)   → COOH
  //   v3 = (-1,  1, -1)   → OH
  //   v4 = ( 1, -1, -1)   → H
  "R-lactic-acid": {
    atoms: [
      { id: "cc", el: "C", pos: [0, 0, 0] },
      // CH3 carbon
      { id: "c_me", el: "C", pos: [0.87, 0.87, 0.87] },
      // COOH carbon
      { id: "c_co", el: "C", pos: [-0.87, -0.87, 0.87] },
      { id: "o_dbl", el: "O", pos: [-1.5, -1.95, 0.5] },
      { id: "o_oh", el: "O", pos: [-1.4, -0.5, 1.95] },
      { id: "h_oh_acid", el: "H", pos: [-2.2, -0.9, 2.25] },
      // Hydroxyl on chiral carbon
      { id: "o_chir", el: "O", pos: [-0.87, 0.87, -0.87] },
      { id: "h_oh", el: "H", pos: [-1.6, 1.4, -1.1] },
      // H on chiral carbon
      { id: "h_chir", el: "H", pos: [0.62, -0.62, -0.62] },
      // Methyl hydrogens
      { id: "h_m1", el: "H", pos: [1.6, 0.4, 1.4] },
      { id: "h_m2", el: "H", pos: [0.4, 1.6, 1.4] },
      { id: "h_m3", el: "H", pos: [1.4, 1.4, 0.4] },
    ],
    bonds: [
      { from: "cc", to: "c_me" },
      { from: "cc", to: "c_co" },
      { from: "cc", to: "o_chir" },
      { from: "cc", to: "h_chir" },
      { from: "o_chir", to: "h_oh" },
      { from: "c_co", to: "o_dbl", order: 2 },
      { from: "c_co", to: "o_oh" },
      { from: "o_oh", to: "h_oh_acid" },
      { from: "c_me", to: "h_m1" },
      { from: "c_me", to: "h_m2" },
      { from: "c_me", to: "h_m3" },
    ],
  },

  // (S)-Lactic acid: mirror image - flip x for every atom
  "S-lactic-acid": {
    atoms: [
      { id: "cc", el: "C", pos: [0, 0, 0] },
      { id: "c_me", el: "C", pos: [-0.87, 0.87, 0.87] },
      { id: "c_co", el: "C", pos: [0.87, -0.87, 0.87] },
      { id: "o_dbl", el: "O", pos: [1.5, -1.95, 0.5] },
      { id: "o_oh", el: "O", pos: [1.4, -0.5, 1.95] },
      { id: "h_oh_acid", el: "H", pos: [2.2, -0.9, 2.25] },
      { id: "o_chir", el: "O", pos: [0.87, 0.87, -0.87] },
      { id: "h_oh", el: "H", pos: [1.6, 1.4, -1.1] },
      { id: "h_chir", el: "H", pos: [-0.62, -0.62, -0.62] },
      { id: "h_m1", el: "H", pos: [-1.6, 0.4, 1.4] },
      { id: "h_m2", el: "H", pos: [-0.4, 1.6, 1.4] },
      { id: "h_m3", el: "H", pos: [-1.4, 1.4, 0.4] },
    ],
    bonds: [
      { from: "cc", to: "c_me" },
      { from: "cc", to: "c_co" },
      { from: "cc", to: "o_chir" },
      { from: "cc", to: "h_chir" },
      { from: "o_chir", to: "h_oh" },
      { from: "c_co", to: "o_dbl", order: 2 },
      { from: "c_co", to: "o_oh" },
      { from: "o_oh", to: "h_oh_acid" },
      { from: "c_me", to: "h_m1" },
      { from: "c_me", to: "h_m2" },
      { from: "c_me", to: "h_m3" },
    ],
  },
};
